import React from "react";

const VerticalPrescriptionPatientDetails = ({
  patientData,
  isVertical21cm,
  opdContent,
}: {
  patientData: any;
  isVertical21cm: boolean;
  opdContent: any;
}) => {
  const patient = patientData?.patientDetails?.[0];

  // ============================================================
  // COMMON CONTAINER
  // ============================================================

  const containerStyle = {
    display: "flex",
    flexDirection: "column" as const,
    width: "100%",
    fontSize: "14px",
    padding: "8px 4px 4px 4px",
    rowGap: "6px",
  };

  // ============================================================
  // COMMON ROW
  // ============================================================

  const rowStyle = {
    display: "grid",
    alignItems: "start",
    width: "100%",
    minWidth: 0,
  };

  // ============================================================
  // LABEL
  // ============================================================

  const labelStyle = {
    whiteSpace: "nowrap" as const,
    fontSize: "12px",
    flexShrink: 0,
    marginRight: "4px",
    paddingTop: "1px",
  };

  // ============================================================
  // VALUE
  // ============================================================

  const valueStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    overflowWrap: "break-word" as const,
    wordBreak: "break-word" as const,
    minWidth: 0,
  };

  // ============================================================
  // FIELD
  // ============================================================

  const fieldStyle = {
    display: "flex",
    alignItems: "flex-start",
    minWidth: 0,
    overflow: "hidden",
  };

  // ============================================================
  // ROW 1
  //
  // NAME = 3 PARTS
  // DATE = 1 PART
  // ============================================================

  const row1Style = {
    ...rowStyle,
    gridTemplateColumns: "2.6fr 1.1fr 1.3fr",
  };

  // ============================================================
  // ROW 2
  //
  // AGE = 1 PART
  // H.C. = 1 PART
  // HEIGHT = 1 PART
  // WEIGHT = 1 PART
  // ============================================================

  const row2Style = {
    ...rowStyle,
    gridTemplateColumns: "1fr 1fr 0.6fr 1.4fr 1fr",
  };

  // ============================================================
  // ROW 3
  //
  // ADDRESS = 2 PARTS
  // CONTACT = 2 PARTS
  // ============================================================

  const row3Style = {
    ...rowStyle,
    gridTemplateColumns: "3fr 2fr",
  };

  return (
    <div style={containerStyle} className="patient-details-grid">
      {/* =====================================================
          ROW 1
          NAME 3 PARTS | DATE 1 PART
      ====================================================== */}

      <div style={row1Style}>
        {/* NAME */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            Name:
          </span>

          <span style={valueStyle}>{patient?.fullName || ""}</span>
        </div>

        {/* EMR */}
        <div style={fieldStyle}>
          <span className="right-label-style-vertical" style={labelStyle}>
            EMR #:
          </span>

          <span style={valueStyle}>{patient?.patientNumber || ""}</span>
        </div>

        {/* DATE */}
        {/* DATE */}
        <div
          style={{
            ...fieldStyle,
            justifyContent: "flex-end",
          }}
        >
          <span className="right-label-style-vertical" style={labelStyle}>
            Date:
          </span>

          <span style={valueStyle}>
            {patientData?.opdDetails?.caseDate || ""}
          </span>
        </div>
      </div>

      {/* =====================================================
          ROW 2
          AGE | H.C. | HEIGHT | WEIGHT
      ====================================================== */}

      <div style={row2Style}>
        {/* AGE */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            Age:
          </span>

          <span style={valueStyle}>
            {patient?.ageYear > 0 ? `${patient.ageYear}y` : ""}

            {patient?.ageMonth > 0 ? ` ${patient.ageMonth}m` : ""}

            {patient?.ageDay > 0 ? ` ${patient.ageDay}d` : ""}
          </span>
        </div>
        {/* SEX */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            Gender:
          </span>

          <span style={valueStyle}>
            {patient?.gender || patient?.sex || ""}
          </span>
        </div>

        {/* H.C. */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            H.C.:
          </span>

          <span style={valueStyle}>
            {opdContent?.examinationVitalParameters?.headCircumference || ""}
            {opdContent?.examinationVitalParameters?.headCircumference
              ? " cm"
              : ""}
          </span>
        </div>

        {/* HEIGHT */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            Height:
          </span>

          <span style={valueStyle}>
            {opdContent?.examinationVitalParameters?.height || ""}
            {opdContent?.examinationVitalParameters?.height ? " cm" : ""}
          </span>
        </div>

        {/* WEIGHT */}
        <div
          style={{
            ...fieldStyle,
            justifyContent: "flex-end",
          }}
        >
          <span className="left-label-style-vertical" style={labelStyle}>
            Weight:
          </span>

          <span style={valueStyle}>
            {opdContent?.examinationVitalParameters?.weight || ""}
            {opdContent?.examinationVitalParameters?.weight ? " kg" : ""}
          </span>
        </div>
      </div>

      {/* =====================================================
          ROW 3
          ADDRESS 2 PARTS | CONTACT 2 PARTS
      ====================================================== */}

      <div style={row3Style}>
        {/* ADDRESS */}
        <div style={fieldStyle}>
          <span className="left-label-style-vertical" style={labelStyle}>
            Address:
          </span>

          <span style={valueStyle}>
            {patient?.area?.length > 0 ? `${patient.area}, ` : ""}

            {patient?.city || ""}
          </span>
        </div>

        {/* CONTACT */}
        <div
          style={{
            ...fieldStyle,
            justifyContent: "flex-end",
          }}
        >
          <span className="right-label-style-vertical" style={labelStyle}>
            Contact:
          </span>

          <span style={valueStyle}>{patient?.primaryContactNumber || ""}</span>
        </div>
      </div>
    </div>
  );
};

export default VerticalPrescriptionPatientDetails;
